import React, { useState } from "react";
import { FaArrowRight } from "react-icons/fa";

const websiteUrl = (value) => {
  try {
    const url = new URL(value);
    return ["http:", "https:"].includes(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
};

const mediaUrl = (media, origin) => {
  const value = media?.cloudinary?.secure_url || media?.url;
  if (!value) return null;
  try {
    return websiteUrl(new URL(value, origin).href);
  } catch {
    return null;
  }
};

const ShowcaseSlider = ({ title, items, collaboration = false, mediaOrigin }) => {
  const [paused, setPaused] = useState(false);
  if (!items.length) return null;

  // Fill a desktop-width track even when only two partners are configured.
  const repeatedItems = items.length > 1
    ? Array.from({ length: Math.max(0, Math.ceil(5 / items.length) - 1) }, () => items).flat()
    : [];
  const renderCard = (item, index, duplicate) => {
    const name = collaboration ? item.website_name : item.title;
    const href = websiteUrl(collaboration ? item.referral_url : item.website_url);
    const image = mediaUrl(collaboration ? item.logo : item.image, mediaOrigin);
    const content = (
      <>
        {image && <img src={image} alt={duplicate ? "" : name} loading="lazy" className={collaboration ? "dm-showcase-logo" : "dm-showcase-image"} />}
        <div className="p-5">
          <h3 className="dm-serif mb-2 text-xl text-[#161310]">{name}</h3>
          {item.description && <p className="mb-3 text-xs leading-relaxed text-[#161310]/65">{item.description}</p>}
          {!collaboration && item.website_name && <p className="mb-2 text-xs font-semibold text-[#161310]/65">{item.website_name}</p>}
          {href && <span className="flex items-center gap-2 text-xs font-bold text-[#bc1535]">{collaboration ? "Visit website" : "Explore"}<FaArrowRight aria-hidden="true" /></span>}
        </div>
      </>
    );
    const className = "dm-showcase-card overflow-hidden rounded-2xl border border-[#161310]/10 bg-white text-left";
    return href ? (
      <a key={`${item.id || "card"}-${index}`} href={href} target="_blank" rel={collaboration ? "noopener noreferrer sponsored" : "noopener noreferrer"} aria-hidden={duplicate || undefined} tabIndex={duplicate ? -1 : undefined} className={`${className} no-underline transition-colors hover:border-[#bc1535]/40`}>{content}</a>
    ) : <article key={`${item.id || "card"}-${index}`} aria-hidden={duplicate || undefined} className={className}>{content}</article>;
  };

  return (
    <section aria-label={title} className="border-t border-[#161310]/10 bg-[#f5efe8]/40 py-8 sm:py-10">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-5 flex items-center justify-between gap-4">
          <h2 className="dm-serif text-3xl text-[#161310] sm:text-4xl">{title}</h2>
          {items.length > 1 && <button type="button" onClick={() => setPaused((value) => !value)} aria-pressed={paused} className="shrink-0 rounded-full border border-[#161310]/20 px-4 py-2 text-xs font-semibold text-[#161310]">{paused ? "Resume slider" : "Pause slider"}</button>}
        </div>
        <div className={`dm-showcase-slider ${items.length > 1 ? "dm-showcase-animated" : ""} ${paused ? "dm-showcase-paused" : ""}`}>
          <div className="dm-showcase-track" style={{ "--showcase-duration": `${Math.max(20, items.length * 6)}s` }}>
            <div className="dm-showcase-group">
              {items.map((item, index) => renderCard(item, index, false))}
              <div className="dm-showcase-repeat" aria-hidden="true">{repeatedItems.map((item, index) => renderCard(item, index, true))}</div>
            </div>
            {items.length > 1 && <div className="dm-showcase-group dm-showcase-duplicate" aria-hidden="true">{[...items, ...repeatedItems].map((item, index) => renderCard(item, index, true))}</div>}
          </div>
        </div>
      </div>
    </section>
  );
};

const DMShowcase = ({ data, mediaOrigin }) => (
  <>
    <ShowcaseSlider title={data?.collaborations_title || "Our collaborations"} items={(data?.collaborations || []).filter((item) => item.website_name)} collaboration mediaOrigin={mediaOrigin} />
    <ShowcaseSlider title={data?.expertise_title || "Our expertise"} items={(data?.expertise || []).filter((item) => item.title)} mediaOrigin={mediaOrigin} />
  </>
);

export default DMShowcase;
